// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Loader,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import { PENDING_INTAKE_TAG } from '@polarissleep/shared';
import { IconAlertCircle, IconChevronLeft, IconChevronRight, IconSearch } from '@tabler/icons-react';
import type { ChangeEvent, JSX } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { usePatientList } from '../../../hooks/usePatientList';
import { PatientListItem } from './PatientListItem';
import classes from './PatientListSidebar.module.css';

const SKELETON_ROWS = 8;

interface PatientListSidebarProps {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onPatientSelect?: (patient: Patient) => void;
  onAddPatient?: () => void;
}

export function PatientListSidebar({
  collapsed = false,
  onToggleCollapsed,
  onPatientSelect,
  onAddPatient,
}: PatientListSidebarProps = {}): JSX.Element {
  const { patientId } = useParams<{ patientId: string }>();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const {
    patients,
    lastContactDates = {},
    isLoading,
    isFetchingMore,
    error,
    hasMore,
    loadMore,
  } = usePatientList({
    search,
    pendingOnly: statusFilter === PENDING_INTAKE_TAG.code,
  });

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.currentTarget.value);
  };

  const showSkeletons = isLoading && patients.length === 0;
  const showEmptyState = !isLoading && !error && patients.length === 0;

  if (collapsed) {
    return (
      <div className={`${classes.container} ${classes.containerCollapsed}`}>
        <Box className={classes.collapsedHeader}>
          <Tooltip label="Expand patient search" position="right">
            <ActionIcon
              aria-label="Expand patient search"
              variant="subtle"
              color="gray"
              size="lg"
              onClick={onToggleCollapsed}
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </Tooltip>
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Box className={classes.searchWrapper}>
        {onAddPatient && (
          <Button fullWidth mb="sm" onClick={onAddPatient}>
            Add patient
          </Button>
        )}
        <TextInput
          aria-label="Search patients"
          placeholder="Search"
          value={search}
          onChange={handleSearchChange}
          leftSection={<IconSearch size={16} />}
          rightSection={
            onToggleCollapsed ? (
              <Tooltip label="Minimize patient search" withArrow>
                <ActionIcon
                  aria-label="Minimize patient search"
                  variant="subtle"
                  color="gray"
                  size="md"
                  onClick={onToggleCollapsed}
                >
                  <IconChevronLeft size={16} />
                </ActionIcon>
              </Tooltip>
            ) : undefined
          }
        />
        <Select
          mt="xs"
          aria-label="Filter patients"
          placeholder="All patients"
          clearable
          value={statusFilter}
          onChange={setStatusFilter}
          data={[
            {
              value: PENDING_INTAKE_TAG.code ?? 'pending-intake',
              label: PENDING_INTAKE_TAG.display ?? 'Pending Intake',
            },
          ]}
        />
      </Box>

      <ScrollArea className={classes.scrollArea} type="auto">
        <Stack gap={6} className={classes.list}>
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} className={classes.alert}>
              {error.message}
            </Alert>
          )}

          {showSkeletons &&
            Array.from({ length: SKELETON_ROWS }).map((_, idx) => (
              <Skeleton key={`skeleton-${idx}`} height={56} radius="sm" />
            ))}

          {!showSkeletons &&
            patients.map((p) => (
              <PatientListItem
                key={p.id}
                patient={p}
                lastContact={p.id ? lastContactDates[p.id] : undefined}
                selected={!onPatientSelect && p.id === patientId}
                onClick={onPatientSelect}
              />
            ))}

          {showEmptyState && (
            <Center className={classes.emptyState}>
              <Text c="dimmed" size="sm">
                No patients found
              </Text>
            </Center>
          )}

          {hasMore && !showSkeletons && (
            <Button variant="subtle" size="sm" onClick={loadMore} loading={isFetchingMore} className={classes.loadMore}>
              Load more
            </Button>
          )}

          {isFetchingMore && !hasMore && (
            <Center className={classes.fetchingMore}>
              <Loader size="sm" />
            </Center>
          )}
        </Stack>
      </ScrollArea>
    </div>
  );
}
